import { useState } from "react";
import { Download, Lock, Loader2 } from "lucide-react";
import { getToken } from "@/api";

const API = process.env.REACT_APP_BACKEND_URL;

export const isPaidPlan = (user) =>
  Boolean(user && user.planStatus === "active" && ["Starter", "Pro", "Elite"].includes(user.plan));

/* PDF downloads are Pro / Elite only; basic stats + daily chart unlock at Starter */
export const hasPremiumAnalytics = (user) =>
  Boolean(user && user.planStatus === "active" && ["Pro", "Elite"].includes(user.plan));

/**
 * The single session-download control used by BOTH the completion card and
 * the practice-history detail view. Free users see a locked variant.
 */
const SessionDownloadButton = ({ sessionId, paid, onLockedClick }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const download = async () => {
    if (!sessionId || busy) return;
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/practice-sessions/${sessionId}/pdf`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (res.status === 403) {
        onLockedClick?.();
        return;
      }
      if (!res.ok) throw new Error("pdf failed");
      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition") || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = match ? match[1] : "abhyas_session.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError("Unable to generate the session PDF. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <button
        data-testid="download-session-btn"
        onClick={paid ? download : onLockedClick}
        disabled={busy || !sessionId}
        className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold shadow-sm transition-[background-color,transform] duration-200 disabled:opacity-60 ${
          paid
            ? "bg-[#16A34A] text-white hover:bg-[#15803D]"
            : "border-2 border-[#E3ECE6] bg-white text-[#647067] hover:border-[#16A34A]/40"
        }`}
      >
        {busy ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : paid ? (
          <Download className="h-4 w-4" />
        ) : (
          <Lock className="h-4 w-4" data-testid="download-lock-icon" />
        )}
        {busy ? "Preparing PDF..." : "Download Session"}
      </button>
      {error && (
        <p data-testid="download-error" className="mt-2 text-xs font-semibold text-red-500">
          {error}
        </p>
      )}
    </div>
  );
};

export default SessionDownloadButton;
